import React, { useEffect, useState } from "react";
import OnboardingScreen, { OnboardingErrorText } from "../OnboardingScreen";
import { OnboardingCircleNextButton, OnboardingGlassButton, OnboardingNextButton } from "../OnboardingButtons";
import type { OnboardingWellnessDraft } from "../../../hooks/useOnboardingWizard";
import {
  CAPACITY_OPTIONS,
  DEMO_WELLNESS_SCORE,
  SLEEP_TIME_RESPONSES,
  WELLNESS_CATEGORIES,
  WELLNESS_FALLBACK_QUESTIONS,
  sorenessLabel,
  wellnessAnswerLabel,
  wellnessCategoryLabel,
} from "../onboardingCopy";
import { wellnessScore } from "../onboardingValidation";

interface FirstWellnessStepProps {
  wellness: OnboardingWellnessDraft;
  submitting: boolean;
  submitError: string | null;
  /** Today's log already exists — the form just advances. */
  alreadyLogged: boolean;
  onChange: (patch: Partial<OnboardingWellnessDraft>) => void;
  onBack: () => void;
  onSubmit: () => void;
}

const WellnessSlider: React.FC<{
  title: string;
  question: string;
  value: number;
  min: number;
  max: number;
  label: string;
  onChange: (value: number) => void;
}> = ({ title, question, value, min, max, label, onChange }) => (
  <div className="flex flex-col gap-2 rounded-lg border border-peak-border bg-peak-bg p-4">
    <div className="flex items-baseline justify-between gap-3">
      <p className="font-funnel text-[16px] font-semibold text-peak-text">{title}</p>
      <span className="font-geist text-[13px] text-peak-text-secondary">{value}</span>
    </div>
    <p className="font-funnel text-[14px] text-peak-text-secondary">{question}</p>
    <input
      type="range"
      min={min}
      max={max}
      step={1}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      aria-label={question}
      className="w-full accent-peak-wellness"
      style={{ accentColor: "#10B981" }}
    />
    <p className="min-h-[20px] font-geist text-[13px] text-peak-text">{label}</p>
  </div>
);

const CheckboxRow: React.FC<{ label: string; checked: boolean; onChange: (checked: boolean) => void }> = ({ label, checked, onChange }) => (
  <label className="flex min-h-[56px] cursor-pointer items-center gap-3.5 rounded-lg border border-peak-border bg-peak-bg px-[18px]">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="checkbox checkbox-sm" />
    <span className="font-funnel text-[16px] text-peak-text">{label}</span>
  </label>
);

/** Intro page then the first real wellness questionnaire (iOS `OnboardingFirstWellnessPage`). */
const FirstWellnessStep: React.FC<FirstWellnessStepProps> = ({ wellness, submitting, submitError, alreadyLogged, onChange, onBack, onSubmit }) => {
  const [page, setPage] = useState<0 | 1>(0);
  const [ringOn, setRingOn] = useState(false);
  const score = wellnessScore(wellness);

  useEffect(() => {
    if (page !== 0) return;
    setRingOn(false);
    const t = setTimeout(() => setRingOn(true), 350);
    return () => clearTimeout(t);
  }, [page]);

  if (page === 0) {
    const radius = 46;
    const circumference = 2 * Math.PI * radius;
    return (
      <OnboardingScreen
        headerBottom={4}
        onBack={onBack}
        footer={
          <div className="flex justify-end">
            <OnboardingCircleNextButton down onClick={() => setPage(1)} />
          </div>
        }
      >
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
          <div className="flex flex-col items-center">
            <p className="font-caveat text-[30px] leading-none text-peak-text-secondary">Add your first</p>
            <h1 className="font-funnel text-[32px] font-bold leading-[1.1] tracking-[-0.6px] text-peak-text">Wellness log</h1>
          </div>
          <p className="font-geist text-[15px] text-peak-text-secondary">No one (or wearable) can tell you how you feel better than you...</p>
          <div className="relative mt-3 h-[108px] w-[108px]">
            <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
              <circle cx="50" cy="50" r={radius} fill="none" stroke="#10B981" strokeOpacity={0.2} strokeWidth={8} />
              <circle
                cx="50"
                cy="50"
                r={radius}
                fill="none"
                stroke="#10B981"
                strokeWidth={8}
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={circumference * (1 - (ringOn ? DEMO_WELLNESS_SCORE / 100 : 0))}
                style={{ transition: "stroke-dashoffset 800ms ease-out" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-geist text-[28px] font-bold leading-none text-peak-text">{DEMO_WELLNESS_SCORE}</span>
              <span className="font-funnel text-[11px] font-medium text-peak-text/75">score</span>
            </div>
          </div>
        </div>
      </OnboardingScreen>
    );
  }

  return (
    <OnboardingScreen
      onBack={() => setPage(0)}
      scroll
      width="medium"
      footer={
        <div className="flex flex-col gap-3">
          <OnboardingErrorText>{submitError}</OnboardingErrorText>
          <OnboardingNextButton
            label={alreadyLogged ? "Continue" : submitting ? "Saving…" : `Save wellness log · ${score}`}
            enabled={!submitting}
            onClick={onSubmit}
          />
          <OnboardingGlassButton label="Back" onClick={() => setPage(0)} enabled={!submitting} />
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="font-funnel text-[32px] font-bold leading-[1.1] tracking-[-0.6px] text-peak-text">
            Wellness <span className="font-caveat text-[34px] font-normal tracking-normal text-peak-text-secondary">Log</span>
          </h1>
          <p className="font-funnel text-[14px] text-peak-text-secondary">Self-reported wellness questionnaire.</p>
          <p className="font-geist text-[12px] text-peak-text-tertiary">
            {new Date().toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </div>

        {alreadyLogged && (
          <p className="rounded-lg border border-peak-border bg-peak-bg-secondary px-4 py-3 font-funnel text-[14px] text-peak-text-secondary">
            Today's wellness log is already saved. Continue when you're ready.
          </p>
        )}

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <WellnessSlider
            title="Sleep duration"
            question="How long did you sleep?"
            value={wellness.sleepTime}
            min={1}
            max={7}
            label={wellnessAnswerLabel(SLEEP_TIME_RESPONSES, wellness.sleepTime)}
            onChange={(sleepTime) => onChange({ sleepTime })}
          />
          {WELLNESS_CATEGORIES.map((category) => (
            <WellnessSlider
              key={category}
              title={wellnessCategoryLabel(category)}
              question={WELLNESS_FALLBACK_QUESTIONS[category].question}
              value={wellness[category]}
              min={1}
              max={7}
              label={wellnessAnswerLabel(WELLNESS_FALLBACK_QUESTIONS[category].answers, wellness[category])}
              onChange={(value) => onChange({ [category]: value } as Partial<OnboardingWellnessDraft>)}
            />
          ))}
          <WellnessSlider
            title="Soreness"
            question="How sore are you today?"
            value={wellness.soreness}
            min={0}
            max={10}
            label={sorenessLabel(wellness.soreness)}
            onChange={(soreness) => onChange({ soreness })}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <CheckboxRow label="Feeling unwell" checked={wellness.sick} onChange={(sick) => onChange({ sick })} />
          <CheckboxRow label="Carrying an injury" checked={wellness.injured} onChange={(injured) => onChange({ injured })} />
        </div>
        {wellness.injured && (
          <div className="flex flex-col gap-2">
            <p className="font-funnel text-[14px] font-medium text-peak-text">What training can you do?</p>
            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Training capacity">
              {CAPACITY_OPTIONS.map((option) => (
                <button
                  key={option.wire}
                  type="button"
                  role="radio"
                  aria-checked={wellness.trainingCapacity === option.wire}
                  onClick={() => onChange({ trainingCapacity: option.wire })}
                  className={`min-h-[48px] rounded-lg border font-funnel text-[14px] font-semibold transition-colors ${
                    wellness.trainingCapacity === option.wire
                      ? "border-peak-text bg-peak-text text-peak-bg"
                      : "border-peak-border bg-peak-bg text-peak-text"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </OnboardingScreen>
  );
};

export default FirstWellnessStep;
